import DDatePicker from 'components/ui/base/datepicker/Datepicker';
import IconTooltip from 'components/ui/base/tooltip/IconTooltip';
import { TextSmall } from 'components/ui/base/typography';
import { FieldHelperProps, FieldInputProps, FieldMetaProps } from 'formik';
import useDeclarationInputFocused from 'hooks/useDeclarationInputFocused';
import { FC, ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useOutletContext } from 'react-router-dom';
import { getFormikProps } from 'views/declarations/utils/form-utils';
import { TemplateCheckboxes } from '../../template/TemplateCheckbox';
import { useRegisterField } from '../../template/CardContext';
import { useTemplateContext } from '../../template/TemplateContext';
import useFieldTemplateMetaData from '../../template/useTemplateViewEditControls';
import useNonAmendableField from 'views/declarations/utils/useNonAmendableField';
import {
    ErrorDiv,
    ErrorLabel,
    FieldWrapper,
    InputLabel,
    InputLabelRequired,
    InputSection,
    LabelDiv,
    MetaSection,
    RefDiv,
    FormatDiv,
    FormItem,
} from '../formInput/DeclarationInput.styles';
import { handleBlur } from '../utils';
import { kebabCase } from 'lodash';
import useDeclarations from 'hooks/useDeclarations';
import { TestProps } from 'core/utils/testTypes';
import moment from 'moment';
import { DatePickerDiv } from './DeclarationDatepicker.styles';
import { StyledInfoCircleFilled } from 'views/declarations/declaration-tabs/declaration-form/Form.styles';
import { useDeclarationValueSource } from '../source/DeclarationSourceContext';
import DeclarationValueSourceIcon from '../source/DeclarationValueSourceIcon';
import SourceIconPrefix from '../source/SourceIconPrefix';

export interface FormDatepickerProps extends TestProps {
    label?: string;
    tooltip?: string;
    icon?: ReactNode;
    refNumber?: string;
    fieldProps: FieldInputProps<any>;
    fieldMeta?: FieldMetaProps<any>;
    fieldHelper?: FieldHelperProps<any>;
    showTime?: boolean;
    viewOnly?: boolean;
    refClicked?: (refNumber: string | number) => void;
    required?: boolean;
    condensed?: boolean;
    onChange?: (value: moment.Moment | null) => void;
    disabled?: boolean;
    showDateFormatLabel?: boolean;
    placeholder?: string;
    specialName?: string; // Used for template meta handle of taxBox34Bis fields as of 13/04/23
    hidden?: boolean;
    getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
}

const DeclarationDatepicker: FC<FormDatepickerProps> = ({
    label,
    refNumber,
    tooltip,
    fieldProps: fieldPropsProp,
    fieldMeta: fieldMetaProp,
    fieldHelper: fieldHelpersProp,
    showTime,
    viewOnly: viewOnlyProp,
    refClicked,
    required = false,
    condensed,
    onChange,
    disabled,
    placeholder,
    specialName,
    hidden,
    testId,
    getPopupContainer,
    showDateFormatLabel = true,
}) => {
    const { t } = useTranslation('common');
    const { setFocused } = useDeclarationInputFocused();
    const outletContext = useOutletContext<{
        amendment?: boolean;
    }>();
    const location = useLocation();
    const inViewOnly = location.pathname.split('/').includes('view-only');
    const { template, templateFormik, form, isViewOnly: isTemplateViewOnly } = useTemplateContext();
    const { declaration } = useDeclarations();

    useRegisterField({ path: fieldPropsProp?.name, required, hidden });
    const { isViewable, isEditable, isInvisible } = useFieldTemplateMetaData(fieldPropsProp?.name);
    const isNonAmendable = useNonAmendableField(fieldPropsProp?.name);

    const { fieldProps, fieldMeta, fieldHelpers } = useMemo(() => {
        if (template && templateFormik && form) {
            const f = getFormikProps(`${form}.defaults.${fieldPropsProp?.name}`, templateFormik);
            return { ...f, fieldHelpers: f.fieldHelper };
        }

        return { fieldProps: fieldPropsProp, fieldMeta: fieldMetaProp, fieldHelpers: fieldHelpersProp };
    }, [template, templateFormik, form, fieldPropsProp, fieldMetaProp, fieldHelpersProp]);

    const handleRefClick = useCallback(() => {
        if (refClicked) {
            refNumber ? refClicked(refNumber) : refClicked(label ?? '');
        }
    }, [refClicked, refNumber, label]);

    const showTooltip = useMemo(() => tooltip && tooltip.length > 0, [tooltip]);

    const viewOnly = useMemo(() => {
        if (template) return isTemplateViewOnly;
        if (outletContext?.amendment) return isViewable;
        return viewOnlyProp || !isEditable || inViewOnly || declaration?.archived;
    }, [
        template,
        isTemplateViewOnly,
        outletContext?.amendment,
        isViewable,
        viewOnlyProp,
        isEditable,
        inViewOnly,
        declaration?.archived,
    ]);

    const dateFormatLabel = useMemo(
        () => <FormatDiv>{showTime ? 'YYYY-MM-DD-HH-MM' : 'YYYY-MM-DD'}</FormatDiv>,
        [showTime]
    );

    const valueSource = useDeclarationValueSource(fieldPropsProp?.name, fieldMeta);
    const sourceIcon = useMemo(
        () => (valueSource ? <DeclarationValueSourceIcon {...valueSource} /> : undefined),
        [valueSource]
    );

    const render = useMemo(
        () => (
            <FieldWrapper $viewOnly={viewOnly} $template={template}>
                {viewOnly ? (
                    <>
                        <MetaSection>
                            <LabelDiv condensed={condensed}>
                                {refNumber && <RefDiv>{refNumber}</RefDiv>}

                                {label && (
                                    <InputLabel>
                                        {required && <InputLabelRequired right>*</InputLabelRequired>}
                                        {label}
                                    </InputLabel>
                                )}

                                {showDateFormatLabel && dateFormatLabel}
                                {showTooltip && (
                                    <IconTooltip
                                        title={label}
                                        tooltip={tooltip ?? t('defaultTooltip')}
                                        icon={<StyledInfoCircleFilled />}
                                        tooltipClicked={handleRefClick}
                                    />
                                )}
                            </LabelDiv>
                            {template && <TemplateCheckboxes disabled fieldPath={fieldPropsProp?.name} />}
                        </MetaSection>
                        <InputSection>
                            <TextSmall data-testid={testId ?? kebabCase(label ?? fieldPropsProp?.name)}>
                                {fieldProps.value
                                    ? (() => {
                                          const parsedDate = moment(fieldProps.value, 'YYYYMMDDHHmm', true);
                                          return parsedDate.isValid()
                                              ? parsedDate.format('YYYY-MM-DD HH:mm')
                                              : moment(fieldProps.value).format('YYYY-MM-DD HH:mm');
                                      })()
                                    : '-'}
                            </TextSmall>
                        </InputSection>
                    </>
                ) : (
                    <>
                        <MetaSection>
                            <LabelDiv condensed={condensed}>
                                {refNumber && <RefDiv>{refNumber}</RefDiv>}

                                {label && (
                                    <InputLabel>
                                        {required && <InputLabelRequired right>*</InputLabelRequired>}
                                        {label}
                                    </InputLabel>
                                )}

                                {showDateFormatLabel && dateFormatLabel}
                                {showTooltip && (
                                    <IconTooltip
                                        title={label}
                                        tooltip={tooltip ?? t('defaultTooltip')}
                                        icon={<StyledInfoCircleFilled />}
                                        tooltipClicked={handleRefClick}
                                    />
                                )}
                            </LabelDiv>
                        </MetaSection>
                        <InputSection>
                            <FormItem validateStatus={fieldMeta?.error && !!fieldMeta?.touched ? 'error' : ''}>
                                <DatePickerDiv>
                                    <SourceIconPrefix icon={sourceIcon}>
                                        <DDatePicker
                                            style={{ width: '100%', borderRadius: condensed ? 6 : 0 }}
                                            id={testId ?? kebabCase(label ?? fieldPropsProp?.name)}
                                            fieldProps={fieldProps}
                                            fieldHelpers={fieldHelpers}
                                            showTime={showTime}
                                            disabled={disabled || isInvisible || isNonAmendable}
                                            placeholder={placeholder}
                                            onFocus={() => setFocused(true)}
                                            onBlur={(e) => {
                                                fieldHelpers?.setTouched(true);
                                                handleBlur(setFocused, fieldProps, e);
                                            }}
                                            onChange={(date) => {
                                                if (onChange) onChange(date);
                                                else
                                                    fieldProps.onChange({
                                                        target: {
                                                            value: date === null ? date : date.toISOString(),
                                                            name: fieldProps.name,
                                                        },
                                                    });
                                            }}
                                            status={fieldMeta?.error && !!fieldMeta?.touched ? 'error' : ''}
                                            condensed={condensed}
                                            getPopupContainer={getPopupContainer}
                                            lock={!!template}
                                        />
                                    </SourceIconPrefix>
                                    {template && (
                                        <TemplateCheckboxes
                                            required={required}
                                            fieldPath={fieldPropsProp?.name}
                                            specialName={specialName}
                                            onChange={onChange}
                                        />
                                    )}
                                </DatePickerDiv>
                                <ErrorDiv
                                    error={!!fieldMeta?.error}
                                    touched={!!fieldMeta?.touched}
                                    condensed={condensed}
                                >
                                    {!!fieldMeta?.error && !!fieldMeta?.touched && (
                                        <ErrorLabel>{fieldMeta?.error}</ErrorLabel>
                                    )}
                                </ErrorDiv>
                            </FormItem>
                        </InputSection>
                    </>
                )}
            </FieldWrapper>
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [fieldMeta?.error, fieldMeta?.touched, fieldMeta?.value, onChange, disabled, viewOnly, sourceIcon]
    );

    if (hidden || (isInvisible && !template)) return null;

    return render;
};

export default DeclarationDatepicker;
